/* eslint-disable react-refresh/only-export-components */
import type {ChangeEvent, ReactElement} from 'react';
import {useContext} from 'react';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {useComponentProp} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {getSectionColumnsPresets, updateColumnsPreset} from '@EmailMakerReact/lib/email-maker-table-library';
import {updateSectionResponsive} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {ResponsiveType} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {ColumnsSelectorDescriptor} from './columns-selector-control.types';
import {DECIMAL_RADIX, TRUTHY_RESPONSIVE_STRINGS} from './columns-selector-control.types';
import {useAppDispatch} from '@/hooks';
import styles from './columns-selector-control.module.scss';

interface ColumnsSelectorControlInnerProps extends EmailMakerControlProps {
  descriptor: ColumnsSelectorDescriptor;
}

function resolveSelectedPresetId(rawPresetId: unknown, fallbackId: number): number {
  if (typeof rawPresetId === 'number') {
    return rawPresetId;
  }
  if (typeof rawPresetId === 'string') {
    return parseInt(rawPresetId, DECIMAL_RADIX) || fallbackId;
  }
  return fallbackId;
}

function resolveIsResponsiveMobile(rawResponsive: unknown): boolean {
  if (typeof rawResponsive === 'boolean') {
    return rawResponsive;
  }
  return typeof rawResponsive === 'string' && TRUTHY_RESPONSIVE_STRINGS.includes(rawResponsive);
}

function ColumnsSelectorControlInner({component, descriptor}: ColumnsSelectorControlInnerProps): ReactElement {
  const {editor, actions, state} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();

  const rawPresetId = useComponentProp(component, EMAIL_MAKER_PROPS.COLUMNS_PRESET);
  const rawResponsive = useComponentProp(component, EMAIL_MAKER_PROPS.IS_RESPONSIVE_MOBILE);

  const selectedPresetId = resolveSelectedPresetId(rawPresetId, descriptor.initialSelectedId);
  const isResponsiveMobile = resolveIsResponsiveMobile(rawResponsive);

  const allPresets = getSectionColumnsPresets();
  const isMobile = state.editorViewport === EditorViewport.MOBILE;

  function getQuickPickIds(): number[] {
    const ids: number[] = [];
    const anchorId = descriptor.previewIdFixedList.includes(selectedPresetId) ? descriptor.initialSelectedId : selectedPresetId;
    ids.push(anchorId);
    for (const fixedId of descriptor.previewIdFixedList) {
      if (!ids.includes(fixedId)) {
        ids.push(fixedId);
      }
    }
    return ids;
  }

  function handlePresetSelect(presetId: number): void {
    if (!editor) {
      return;
    }
    component.set(EMAIL_MAKER_PROPS.COLUMNS_PRESET, presetId);
    updateColumnsPreset(editor, presetId, component);
  }

  function handleViewAllClick(): void {
    if (isMobile) {
      dispatch(openPanelInModal({modalId: MODALS_IDS.EMAIL_MAKER_COLUMNS_ALL_SHEET, panelProps: {}}));
    } else {
      actions.openRightPanelColumnsAll();
    }
  }

  function handleResponsiveToggle(e: ChangeEvent<HTMLInputElement>): void {
    if (!editor) {
      return;
    }
    updateSectionResponsive(editor, {
      component,
      responsive: e.target.checked,
      type: ResponsiveType.MOBILE,
    });
  }

  function renderPresetTile(presetId: number): ReactElement | null {
    const preset = allPresets.find((p) => p.id === presetId);
    if (!preset) {
      return null;
    }
    const isSelected = preset.id === selectedPresetId;
    return (
      <button
        aria-pressed={isSelected}
        className={`preset-wrapper js-comp-preset ${styles.presetTile} ${isSelected ? styles.presetTileSelected : ''}`}
        data-preset-id={preset.id}
        key={preset.id}
        onClick={(): void => {
          handlePresetSelect(preset.id);
        }}
        type="button"
      >
        {preset.compValues.map((widthPct, idx) => (
          <div className={`preset-comp ${styles.presetColumn}`} key={idx} style={{width: `${widthPct}%`}} />
        ))}
      </button>
    );
  }

  function renderResponsiveToggle(): ReactElement {
    const inputId = `pmw-stack-mobile-${component.ccid}`;
    return (
      <div className={styles.toggleContainer}>
        <label className={styles.toggleLabel} htmlFor={inputId}>
          <Text size={TextSize.MD} val={window.i18next.t('pmwjs_stack_layout_mobile')} />
        </label>
        <div className={styles.toggle}>
          <input
            aria-label={window.i18next.t('pmwjs_stack_layout')}
            checked={isResponsiveMobile}
            className={`tgl tgl-mobile ${styles.tgl}`}
            id={inputId}
            onChange={handleResponsiveToggle}
            type="checkbox"
          />
          <label aria-label={window.i18next.t('pmwjs_stack_layout')} className={`tgl-btn ${styles.tglBtn}`} htmlFor={inputId} />
        </div>
      </div>
    );
  }

  const quickPickIds = getQuickPickIds();

  return (
    <div className={styles.wrapper}>
      <div className={`col-presets ${styles.presetRow}`}>{quickPickIds.map((id) => renderPresetTile(id))}</div>
      {renderResponsiveToggle()}
      <Button
        customClasses={`-column-selector js-cta-text ${styles.viewAllBtn}`}
        customIcon={<Icon className={styles.viewAllIcon} icon="icon-caret-right" size={IconSize.SIZE_ICON_16} />}
        iconPlacement={IconPlacement.RIGHT}
        onClick={handleViewAllClick}
        size={ButtonSize.SMALL}
        text={window.i18next.t('pmwjs_view_all')}
        type={ButtonType.TERTIARY}
      />
    </div>
  );
}

export function makeColumnsSelectorControl(descriptor: ColumnsSelectorDescriptor): EmailMakerControl {
  return createDescriptorControl(ColumnsSelectorControlInner, descriptor);
}
